/**
 * Redux store configuration — offline-first edition.
 *
 * redux-persist persists the `cart` slice to localStorage so cart contents
 * survive full page reloads and are instantly available when the user returns
 * (even offline).  Auth, nav, and other server-derived slices are NOT
 * persisted — they rehydrate via TanStack Query / HTTP-only cookies.
 *
 * @module app/store
 */

import { configureStore }              from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER,
}                                      from 'redux-persist';
import storage                         from 'redux-persist/lib/storage'; // localStorage

import authReducer        from '../features/auth/authSlice';
import productReducer     from '../features/products/productSlice';
import cartReducer        from '../features/cart/cartSlice';
import orderReducer       from '../features/orders/orderSlice';
import adminReducer       from '../features/admin/adminSlice';
import pageReducer        from '../features/pages/pageSlice';
import articleReducer     from '../features/articles/articleSlice';
import navReducer         from '../features/nav/navSlice';
import footerReducer      from '../features/footer/footerSlice';
import siteSettingsReducer from '../features/site-settings/siteSettingsSlice';
import seoReducer         from '../features/seo/seoSlice';
import redirectReducer    from '../features/redirects/redirectSlice';
import seoDashboardReducer from '../features/seo-dashboard/seoDashboardSlice';
import brandingReducer    from '../features/branding/brandingSlice';
import applicationReducer from '../features/applications/applicationSlice';
import maintenanceReducer from '../features/maintenance/maintenanceSlice';

// ── Persist cart so items survive page reloads and offline visits ─────────────
const cartPersistConfig = {
  key:     'afriBrightCart',
  storage,
  version: 1,
};

const store = configureStore({
  reducer: {
    auth:         authReducer,
    products:     productReducer,
    cart:         persistReducer(cartPersistConfig, cartReducer),
    orders:       orderReducer,
    admin:        adminReducer,
    pages:        pageReducer,
    articles:     articleReducer,
    nav:          navReducer,
    footer:       footerReducer,
    siteSettings: siteSettingsReducer,
    seo:          seoReducer,
    redirects:    redirectReducer,
    seoDashboard: seoDashboardReducer,
    branding:     brandingReducer,
    applications: applicationReducer,
    maintenance:  maintenanceReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // redux-persist dispatches these non-serializable action types internally
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);
export default store;

// Developed and Designed by Trophy Developers
